import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { getCurrentLocale } from "../../i18n";
import { Settings, LogOut, Monitor, Moon, Sun, Globe, ChevronDown } from "lucide-react";
import { useDarkModeContext } from "../../contexts/DarkModeContext";
import type { User } from "../../types";

interface ProfileBubbleProps {
  user: User;
  onSettingsClick: () => void;
  onLogout: () => void;
  /** Langue actuelle (owner) pour afficher la sélection ; si fourni, la persister via onOwnerLocaleChange */
  ownerLocale?: "fr" | "en" | "es";
  onOwnerLocaleChange?: (locale: "fr" | "en" | "es") => void;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getAvatarColor(name: string): string {
  const colors = [
    "from-blue-500 to-indigo-600",
    "from-violet-500 to-purple-600",
    "from-fuchsia-500 to-pink-600",
    "from-emerald-500 to-teal-600",
    "from-amber-500 to-orange-500",
    "from-rose-500 to-red-500",
    "from-sky-500 to-blue-600",
    "from-cyan-500 to-teal-600",
  ];
  const hash = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
}

export function ProfileBubble({ user, onSettingsClick, onLogout, ownerLocale, onOwnerLocaleChange }: ProfileBubbleProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, right: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { themeMode, setThemeMode } = useDarkModeContext();
  const { i18n, t } = useTranslation();
  const currentLang: "fr" | "en" | "es" = getCurrentLocale();

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 10,
        right: window.innerWidth - rect.right,
      });
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (!buttonRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
        setIsOpen(false);
      }
    }
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const initials = getInitials(user.name);
  const avatarColor = getAvatarColor(user.name);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setPosition({ top: rect.bottom + 10, right: window.innerWidth - rect.right });
          }
          setIsOpen(!isOpen);
        }}
        className="relative flex items-center gap-2 rounded-full outline-none transition-all duration-200 focus-visible:ring-2 focus-visible:ring-blue-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 hover:opacity-90 active:scale-[0.98]"
        title={user.name}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-full ring-2 ring-gray-200/60 dark:ring-white/10">
          {user.avatar_url ? (
            <img src={user.avatar_url} alt={user.name} className="h-full w-full object-cover" />
          ) : (
            <div className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${avatarColor} text-white font-semibold text-sm`}>
              {initials}
            </div>
          )}
        </div>
        <ChevronDown
          size={18}
          className={`hidden text-gray-500 dark:text-slate-400 transition-transform duration-200 sm:block ${isOpen ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[9999] origin-top-right"
            style={{ top: position.top, right: position.right }}
          >
            <div
              className="w-[280px] animate-in fade-in slide-in-from-top-2 duration-200 rounded-2xl border border-gray-200/90 dark:border-white/10 bg-white/98 dark:bg-slate-800/98 shadow-xl shadow-gray-300/40 dark:shadow-black/40 backdrop-blur-xl"
              role="menu"
            >
              {/* En-tête profil */}
              <div className="border-b border-gray-100 dark:border-white/[0.06] px-4 py-4">
                <div className="flex items-center gap-3">
                  <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-xl ring-2 ring-gray-100 dark:ring-white/10">
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt={user.name} className="h-full w-full object-cover" />
                    ) : (
                      <div className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${avatarColor} text-white font-semibold text-base`}>
                        {initials}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{user.name}</p>
                    {user.email && (
                      <p className="truncate text-xs text-gray-500 dark:text-slate-400">{user.email}</p>
                    )}
                    <span className="mt-1 inline-block rounded-full bg-gray-100 dark:bg-white/10 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:text-slate-400">
                      {user.role === "admin" && user.is_owner && t("common.roleOwner")}
                      {user.role === "admin" && !user.is_owner && t("common.roleAdmin")}
                      {user.role === "partner" && t("common.rolePartner")}
                      {user.role === "installer" && t("common.roleInstaller")}
                    </span>
                  </div>
                </div>
              </div>

              <div className="py-2">
                <p className="px-4 pb-1.5 pt-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-slate-500">
                  {t("common.appearance")}
                </p>
                <div className="mx-3 mb-2 rounded-xl bg-gray-50 dark:bg-white/[0.04] p-1 border border-gray-100/80 dark:border-white/[0.06]">
                  <div className="grid grid-cols-3 gap-0.5">
                    {(["system", "light", "dark"] as const).map((mode) => {
                      const Icon = mode === "system" ? Monitor : mode === "light" ? Sun : Moon;
                      const label = mode === "system" ? t("common.themeSystem") : mode === "light" ? t("common.themeLight") : t("common.themeDark");
                      const isSelected = themeMode === mode;
                      return (
                        <button
                          key={mode}
                          type="button"
                          onClick={() => setThemeMode(mode)}
                          className={`flex flex-col items-center gap-1 rounded-lg py-2 px-1.5 text-[11px] font-medium transition-all duration-150 ${
                            isSelected
                              ? "bg-white dark:bg-white/10 text-gray-900 dark:text-white shadow-sm ring-1 ring-gray-200/60 dark:ring-white/10"
                              : "text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 hover:bg-gray-100/80 dark:hover:bg-white/[0.06]"
                          }`}
                        >
                          <Icon size={18} className="shrink-0" strokeWidth={1.5} />
                          <span className="truncate w-full text-center">{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <p className="px-4 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-slate-500">
                  {t("settings.language")}
                </p>
                <div className="mx-3 mb-3 rounded-xl bg-gray-50 dark:bg-white/[0.04] p-1 border border-gray-100/80 dark:border-white/[0.06]">
                  <div className="grid grid-cols-3 gap-0.5">
                    {(["fr", "en", "es"] as const).map((lang) => {
                      const isSelected = currentLang === lang;
                      const label = lang === "fr" ? t("common.langFr") : lang === "en" ? t("common.langEn") : t("common.langEs");
                      return (
                        <button
                          key={lang}
                          type="button"
                          onClick={() => {
                            i18n.changeLanguage(lang);
                            onOwnerLocaleChange?.(lang);
                          }}
                          className={`flex items-center justify-center gap-1 rounded-lg py-2 px-2 text-[11px] font-medium transition-all duration-150 ${
                            isSelected
                              ? "bg-white dark:bg-white/10 text-gray-900 dark:text-white shadow-sm ring-1 ring-gray-200/60 dark:ring-white/10"
                              : "text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 hover:bg-gray-100/80 dark:hover:bg-white/[0.06]"
                          }`}
                        >
                          <Globe size={14} className="shrink-0 opacity-70" strokeWidth={1.5} />
                          <span className="truncate">{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mx-2 border-t border-gray-100 dark:border-white/[0.06] pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setIsOpen(false);
                      onSettingsClick();
                    }}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-gray-700 dark:text-slate-300 transition-colors hover:bg-gray-100 dark:hover:bg-white/[0.06] active:bg-gray-100/80 dark:active:bg-white/[0.08]"
                  >
                    <Settings size={18} strokeWidth={1.5} className="text-gray-500 dark:text-slate-400" />
                    {t("common.settings")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsOpen(false);
                      onLogout();
                    }}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-red-600 dark:text-red-400 transition-colors hover:bg-red-50 dark:hover:bg-red-500/10 active:bg-red-100/80 dark:active:bg-red-500/20"
                  >
                    <LogOut size={18} strokeWidth={1.5} />
                    {t("common.logout")}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
